

// src/pages/courtowner/CourtOwnerNotifications.js
import React, { useEffect, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";
import { courtOwnerApi } from "../../api/courtOwnerApi";
import { FaSync } from "react-icons/fa";
const MAX_NOTIFICATIONS = 100;

/* ==== Helpers ==== */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatTime12 = (t) => {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  const hour = parseInt(h, 10);
  const minutes = m ? m.replace(/[^\d]/g, "") : "00";
  if (Number.isNaN(hour)) return "";
  return `${hour % 12 || 12}:${minutes.padStart(2, "0")} ${
    hour >= 12 ? "PM" : "AM"
  }`;
};

const formatTimeLabel = (createdAt) => {
  if (!createdAt) return "";
  const now = new Date();
  const then = new Date(createdAt);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = now - then;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 24) {
    const hrs = diffHours <= 0 ? 1 : diffHours;
    return `${hrs} Hrs`;
  }
  const days = diffDays <= 0 ? 1 : diffDays;
  return `${days} Days`;
};

// Use populated user & fallbacks
const getBookerNameSoft = (b) =>
  b?.userId?.name ||
  b?.userId?.fullName ||
  b?.userId?.username ||
  b?.userName ||
  b?.playerName ||
  b?.customerName ||
  b?.user?.name ||
  b?.userDetails?.name ||
  b?.name ||
  "Unknown Player";

const normalizeName = (s) => (s || "").toLowerCase().replace(/\s+/g, "");

// Use booking.status from DB
const isBookingCancelled = (b) => {
  if (!b) return false;
  return b.status === "cancelled";
};

/* ==== Component ==== */
export default function CourtOwnerNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // 'all' | 'upcoming' | 'cancelled'

  const fetchNotificationsFromBookings = async () => {
    try {
      setLoading(true);
      setError("");

      // 1) All venues of this court owner
      const venues = (await courtOwnerApi.getVenues()) || [];

      // 2) For each venue: get courts -> get bookings
      const allPerVenue = await Promise.all(
        venues.map(async (v) => {
          try {
            const courtRes = await courtOwnerApi.getAllCourts(v._id);
            const courts = Array.isArray(courtRes)
              ? courtRes
              : courtRes.courts || [];

            const perCourtBookings = await Promise.all(
              courts.map(async (c) => {
                try {
                  const res = await courtOwnerApi.getCourtBookings(
                    v._id,
                    c._id
                  );

                  const list = Array.isArray(res) ? res : res.bookings || [];

                  return list.map((b) => {
                    const userName = getBookerNameSoft(b);

                    const slotLabel = b.courtLabel
                      ? b.courtLabel
                      : `Court ${
                          b.courtName || c.courtName || c.name || ""
                        }`.trim();

                    // prefer populated venue name if present
                    const venueName =
                      b.venueId?.name ||
                      b.venueName ||
                      v.name ||
                      "Unknown Venue";

                    const bookingDate = b.date || b.bookingDate;
                    const createdAt = b.createdAt || bookingDate || new Date();

                    const startTime24 = b.startTime;
                    const endTime24 = b.endTime;

                    const cancelled = isBookingCancelled(b);
                    const status = cancelled ? "cancelled" : "confirmed";

                    let bucket = "upcoming";
                    if (!cancelled && bookingDate) {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      const bookingD = new Date(bookingDate);
                      if (!Number.isNaN(bookingD.getTime())) {
                        const onlyDate = new Date(
                          bookingD.getFullYear(),
                          bookingD.getMonth(),
                          bookingD.getDate()
                        );
                        if (onlyDate < today) {
                          bucket = "past";
                        }
                      }
                    }
                    if (cancelled) {
                      bucket = "cancelled";
                    }

                    return {
                      _id: b._id || `${v._id}-${c._id}-${Math.random()}`,
                      userName,
                      slotLabel,
                      venueName,
                      bookingDate,
                      startTime24,
                      endTime24,
                      status, // confirmed | cancelled
                      createdAt,
                      bucket, // upcoming | cancelled | past
                    };
                  });
                } catch (err) {
                  console.error("Error fetching court bookings:", err);
                  return [];
                }
              })
            );

            return perCourtBookings.flat();
          } catch (err) {
            console.error("Error fetching courts:", err);
            return [];
          }
        })
      );

      const allBookings = allPerVenue.flat();
      console.log("🔔 Raw bookings for notifications:", allBookings);

      // 🔹 FILTER: only last 7 days (based on createdAt; fallback to bookingDate)
      const now = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(now.getDate() - 7);

      const recentBookings = allBookings.filter((n) => {
        const base = n.createdAt || n.bookingDate;
        if (!base) return false;
        const d = new Date(base);
        if (Number.isNaN(d.getTime())) return false;
        return d >= sevenDaysAgo;
      });

      // 3) De-duplicate
      const uniqueMap = new Map();
      recentBookings.forEach((n) => {
        const key = [
          n.venueName,
          normalizeName(n.slotLabel),
          n.bookingDate && String(n.bookingDate).slice(0, 10),
          n.startTime24,
          n.endTime24,
          n.status,
        ].join("|");

        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, n);
        }
      });

      let finalList = Array.from(uniqueMap.values());

      // 4) Sort latest first and trim
      finalList.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      finalList = finalList.slice(0, MAX_NOTIFICATIONS);

      setNotifications(finalList);
    } catch (err) {
      console.error("Error building notifications from bookings:", err);
      setError("Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotificationsFromBookings();
  }, []);

  const filteredNotifications = notifications.filter((n) => {
    if (filter === "all") return true;
    if (filter === "upcoming") return n.bucket === "upcoming";
    if (filter === "cancelled") return n.bucket === "cancelled";
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:block w-64 bg-white shadow">
        <CourtOwnerSidebar />
      </aside>

      {/* Main */}
      <main className="flex-1 lg:pl-0">
        {/* Top bar – same style as Block Slots (Bulk) */}
        <div className="bg-[#0f2250] text-white sticky top-0 py-1 z-20">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* Mobile sidebar trigger / content */}
              <div className="lg:hidden">
                <CourtOwnerSidebar />
              </div>
              <h1
                className="text-lg font-semibold  lg:ms-0"
                style={{ marginTop: 10 }}
              >
                Notifications
              </h1>
            </div>

            {/* <button
              onClick={fetchNotificationsFromBookings}
              className="text-xs sm:text-sm px-10 py-1.5 me-2 rounded-md bg-white/10 hover:bg-white/20 border border-white/40"
            >
            <FaSync className="animate-spin" />
            </button> */}
          </div>
        </div>

        {/* Content wrapper – like form card in Block Slots (Bulk) */}
        <section className="max-w-5xl mx-auto px-4 pb-16 mt-4">
          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "all", label: "All" },
              { id: "upcoming", label: "Upcoming" },
              { id: "cancelled", label: "Cancelled" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setFilter(opt.id)}
                className={`text-xs sm:text-sm px-4 py-1.5 rounded-full border transition ${
                  filter === opt.id
                    ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* States */}
          {loading && (
            <p className="text-gray-500 text-sm sm:text-base">
              Loading notifications...
            </p>
          )}

          {error && !loading && (
            <p className="text-red-500 mb-4 text-sm sm:text-base">{error}</p>
          )}

          {!loading && !error && filteredNotifications.length === 0 && (
            <p className="text-gray-500 text-sm sm:text-base">
              {filter === "upcoming"
                ? "No upcoming bookings."
                : filter === "cancelled"
                ? "No cancelled bookings."
                : "No notifications yet."}
            </p>
          )}

          {/* List */}
          {!loading && !error && filteredNotifications.length > 0 && (
            <div className="bg-white rounded-2xl shadow-md divide-y w-full overflow-hidden">
              {filteredNotifications.map((n) => (
                <div
                  key={n._id}
                  className="flex items-start gap-3 sm:gap-4 px-4 py-3 sm:px-6 sm:py-4"
                >
                  {/* Icon */}
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 ${
                      n.status === "confirmed"
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {n.status === "confirmed" ? <FaCheck /> : <FaTimes />}
                  </div>

                  {/* Text + Date/Time + Age */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                      <span className="font-semibold">{n.userName}</span>{" "}
                      {n.status === "confirmed"
                        ? "has confirmed "
                        : "has cancelled "}
                      {n.slotLabel}
                      {n.venueName ? ` at ${n.venueName}` : ""}
                    </p>

                    <div className="mt-1 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 justify-between">
                      <p className="text-xs sm:text-sm text-gray-500">
                        {formatDate(n.bookingDate)} ·{" "}
                        {formatTime12(n.startTime24)} -{" "}
                        {formatTime12(n.endTime24)}
                      </p>
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        {formatTimeLabel(n.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}



